import { useState, useEffect, useMemo } from "react";
import { isAfter, isBefore } from "date-fns";
import { api } from "evergis/api";
import { layerInfos } from "resources/layers";
import { FeatureLayerServiceInfoDc, AggregationFunction } from "@evergis/api";
import { useProjectLayersToRender } from "hooks/project/useProjectLayers";
import { Layer } from "../reducers";
import { createCondition } from "./useAxisHandler";
import { DEFAULT_ID_ATTRIBUTE_NAME, GEOMETRY_ATTRIBUTE } from "../../../evergis/constants";

export const getDateLayerAttributes = ({ layerDefinition: { attributes: attr } }: FeatureLayerServiceInfoDc) =>
  Object.keys(attr).filter(attrKey => attr[attrKey].type === "DateTime");

export const getDataLayerAggregationAttributes = ({
  layerDefinition: { attributes: attr },
}: FeatureLayerServiceInfoDc) =>
  Object.keys(attr)
    .filter(
      attrKey => attrKey !== "coordinates" && attrKey !== DEFAULT_ID_ATTRIBUTE_NAME && attrKey !== GEOMETRY_ATTRIBUTE,
    )
    .map(attrKey => ({ name: attrKey, type: attr[attrKey].type }));

type LayerList = FeatureLayerServiceInfoDc & { isVisible?: boolean };

export const useLayerData = () => {
  const data = useProjectLayersToRender();
  const { data: layData, loading, error } = layerInfos.use({ input: data.map(({ name }) => name) });
  const layersData = layData as LayerList[];

  const {
    layersData: memoLayersData,
    loading: memoLoading,
    error: memoError,
  } = useMemo(() => {
    if (layersData && layersData.length > 0) {
      return {
        layersData: layersData
          .filter(layer => layer.layerDefinition && getDateLayerAttributes(layer).length > 0)
          .map(layer => {
            const { name } = layer;
            const dataLayer = data.find(({ name: nameData }) => nameData === name);

            return { ...layer, isVisible: dataLayer ? dataLayer.isVisible : true };
          }),
        loading,
        error,
      };
    }

    return { layersData, loading, error };
  }, [data, error, layersData, loading]);

  return { layersData: memoLayersData, loading: memoLoading, error: memoError };
};

export const createTickProps = (
  layer: Layer,
  startDate: Date,
  endDate: Date,
): {
  name: string;
  attribute: string;
  condition: string;
  types: AggregationFunction[];
} => {
  const condition = createCondition(layer, startDate, endDate);

  return {
    name: layer.id,
    attribute: layer.aggregateAttr,
    condition: condition.condition,
    types: [layer.aggregateFunc],
  };
};

const fetchMinMax = async layer => {
  try {
    const statistic = await api.statistic.getStatistic({
      name: layer.id,
      types: [AggregationFunction.Min, AggregationFunction.Max],
      attribute: layer.attribute,
    });
    const { statisticsResult } = statistic;

    return statisticsResult;
  } catch (e) {
    return null;
  }
};

export const useLayersDateRange = (layers: Layer[]) => {
  const [minMax, setMinMax] = useState<[Date, Date][]>([]);
  const [loading, setLoading] = useState<boolean>(null);

  useEffect(() => {
    if (layers) {
      setLoading(true);
      Promise.all(
        layers.map(layer => {
          if (layer.id && layer.attribute) {
            return fetchMinMax(layer);
          }
        }),
      )
        .then(data => {
          setMinMax(
            data
              .filter(x => x)
              .map(dataItem => [
                new Date(dataItem.find(({ type }) => type === AggregationFunction.Min).value),
                new Date(dataItem.find(({ type }) => type === AggregationFunction.Max).value),
              ]),
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [layers]);

  const range = useMemo(
    () =>
      minMax.reduce(([accStart, accEnd], [start, end]) => {
        return [isAfter(accStart, start) ? start : accStart, isBefore(accEnd, end) ? end : accEnd];
      }, minMax[0]),
    [minMax],
  );

  return { loading, range };
};

export const useGetMaxDateMinDate = (layer: Layer) => {
  const [minMax, setMinMax] = useState<[Date, Date]>([null, null]);
  const [loading, setLoading] = useState<boolean>(null);

  useEffect(() => {
    if (layer && layer.id && layer.attribute) {
      setLoading(true);
      fetchMinMax(layer)
        .then(data => {
          setMinMax([
            new Date(data.find(({ type }) => type === "Min").value),
            new Date(data.find(({ type }) => type === "Max").value),
          ]);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [layer]);

  return { minMax, loading };
};

export { useAxisHandler } from "./useAxisHandler";
export { useNextStepHandler } from "./useAxisHandler";
export { usePrevStepHandler } from "./useAxisHandler";
export { useKeyboardAxisControl } from "./useAxisHandler";
export { usePlayTicks } from "./useAxisHandler";
export { useCallbackLayerDispatchers } from "./layerCardHandler";
